import {CounterLabel, Heading, Link as PrimerLink} from '@primer/react'
import {Link} from '@github-ui/react-router'
import styles from './SidebarSection.module.css'
import {clsx} from 'clsx'

interface SidebarSectionProps {
  title?: React.ReactElement | string
  href?: string
  count?: number | string
  /** Number of additional items beyond what's displayed (renders "+ N items" link). */
  moreCount?: number
  /** Label for the "more" link, e.g. "releases" or "contributors". */
  moreLabel?: string
  children?: React.ReactNode
}

/** Shared wrapper for sidebar sections: renders a heading (optionally linked with a counter), body, and optional "more" link. */
export function SidebarSection({
  title,
  href,
  count,
  moreCount,
  moreLabel,
  children,
  className,
}: SidebarSectionProps & Omit<React.HTMLAttributes<HTMLDivElement>, keyof SidebarSectionProps>) {
  return (
    <div className={clsx(styles.sidebarSection, className)}>
      {title && (
        <Heading as="h2" variant="small" className={styles.sectionHeading}>
          {href ? (
            <span className={styles.headingLinkWrapper}>
              <PrimerLink muted as={Link} to={href} className={styles.headingLink}>
                <span>{title}</span>
              </PrimerLink>
              {!!count && (
                <CounterLabel className="ml-1">
                  {typeof count === 'number' ? count.toLocaleString() : count}
                </CounterLabel>
              )}
            </span>
          ) : (
            <span>{title}</span>
          )}
        </Heading>
      )}
      {children}
      {moreCount != null && moreCount > 0 && href != null && (
        <div className={styles.moreLink}>
          <PrimerLink as={Link} to={href} className="text-small">
            <span>{`+ ${moreCount.toLocaleString()} ${moreLabel ?? 'more'}`}</span>
          </PrimerLink>
        </div>
      )}
    </div>
  )
}
